import { useState, useEffect, useCallback } from 'react';
import { useOrganizationList, useClerk } from '@clerk/clerk-react';
import { resetAnalytics } from '@tagadapay/analytics';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Navigate, useLocation } from 'react-router-dom';
import { CreateAccount } from '@/components/ui/create-account';
import { PostSignUpOnboarding } from '@/components/onboarding';
import { Loader2 } from 'lucide-react';
import {
  getPostSignupOnboardingDone,
  setPostSignupOnboardingDone,
} from '@/lib/post-signup-onboarding';
import { SIGNUP_STORAGE_KEY } from '@/app/sign-up-new/constants';
import type { SignUpFormData } from '@/app/sign-up-new/types';
import { trpc } from '@/lib/api/trpc';
import { brandingConfig } from '@/config/branding';
import { getPostAuthOrgPath } from '@/hooks/use-privileged-staff-bypass';
import { currentPartnerBrand } from '@/config/current-partner';
import { useAccountRestrictions } from '@/hooks/use-account-restrictions';
import {
  captureAndPersistPartnerFromUrl,
  clearCapturedPartner,
  resolveSignupPartner,
} from '@/lib/utils/captured-partner';

const showPostSignupOnboarding =
  brandingConfig.features?.showHub === true || brandingConfig.features?.showCalCom === true;

const getSignupWizardData = (): SignUpFormData | null => {
  try {
    const raw = sessionStorage.getItem(SIGNUP_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * No Account Page - shown when user signs in/up but has no organizations
 * This is different from /create-account which is accessed from the org switcher
 * Now integrated with onboarding system to preserve user intent.
 * Also handles auto-org creation when coming from the new sign-up wizard via Google OAuth.
 */
function NoAccountPage({ accountUnlinked = false }: { accountUnlinked?: boolean }) {
  const wizardData = getSignupWizardData();
  const hasWizardData = !!wizardData?.aboutYou?.email;

  const [showCreateOrg, setShowCreateOrg] = useState(
    () => hasWizardData || getPostSignupOnboardingDone() || !showPostSignupOnboarding,
  );
  const [autoCreating, setAutoCreating] = useState(false);
  // Latches once the wizard's account name turns out to be taken inside the
  // partner. Without it the effect below would re-run auto-creation as soon
  // as `autoCreating` flips back to false, hammering the availability check.
  const [nameTaken, setNameTaken] = useState(false);

  const { userMemberships, isLoaded, createOrganization, setActive } = useOrganizationList({
    userMemberships: {
      pageSize: 10,
    },
  });
  const { signOut } = useClerk();
  const utils = trpc.useUtils();
  const provisionAccountMutation = trpc.account.provisionAccount.useMutation();

  // Capture `?partner=<slug>` if the user landed here directly via
  // a partner deep-link (e.g. a partner-emailed invite that goes
  // straight to /no-organization after Clerk auth). Idempotent.
  // Persisted to cookie + localStorage; read back at provisioning
  // time below. See `lib/utils/captured-partner.ts`.
  useEffect(() => {
    captureAndPersistPartnerFromUrl();
  }, []);

  const autoCreateOrg = useCallback(async () => {
    if (!wizardData || !createOrganization || autoCreating) return

    setAutoCreating(true)
    try {
      const accountName =
        wizardData.createAccount?.accountName ||
        `${wizardData.aboutYou.firstName}'s account`

      // Another merchant of the same partner already uses this name.
      // Abandon the silent auto-creation and fall through to the manual
      // form, where the merchant sees the collision and can pick another
      // name — creating a second org here would strand them in an empty
      // CRM. Unaffiliated merchants are never blocked.
      // Fails OPEN, like the manual form: a failed check must not stop a
      // signup. It matters more here — throwing would leave `nameTaken`
      // false, and the effect below would call this again on every render.
      let nameIsTaken = false
      try {
        const availability = await utils.account.checkNameAvailability.fetch({
          name: accountName,
          partner: resolveSignupPartner(currentPartnerBrand()),
        })
        nameIsTaken = !availability.available
      } catch (checkError) {
        console.warn('Name availability check failed, continuing:', checkError)
      }
      if (nameIsTaken) {
        setNameTaken(true)
        return
      }

      const org = await createOrganization({ name: accountName })

      if (org?.id && setActive) {
        await setActive({ organization: org.id })
      }

      // Provision the DB account row before redirecting. Forward the
      // resolved partner attribution: host (Partner Mode) wins over
      // URL-captured. Server validates against the `partners` DB table
      // (via `isKnownPartnerAsync`) and silently drops unknown slugs.
      try {
        await provisionAccountMutation.mutateAsync({
          partner: resolveSignupPartner(currentPartnerBrand()),
        })
        clearCapturedPartner()
      } catch (provisionError) {
        console.error('Failed to provision account during auto-create:', provisionError)
      }

      sessionStorage.removeItem(SIGNUP_STORAGE_KEY)

      if (org?.slug) {
        // New account: go straight to the activation funnel. The CRM is
        // gated until a billing card exists, so skip the /org/:slug hop
        // (which would only bounce here) and avoid a hard reload flash.
        window.location.href = getPostAuthOrgPath(org.slug, false)
        return
      }
    } catch (err) {
      console.error('Auto org creation failed:', err)
    } finally {
      setAutoCreating(false)
    }
  }, [wizardData, createOrganization, setActive, autoCreating, provisionAccountMutation, utils])

  useEffect(() => {
    if (hasWizardData && isLoaded && !autoCreating && !nameTaken) {
      const firstOrg = userMemberships?.data?.[0]?.organization
      if (!firstOrg?.slug) {
        autoCreateOrg()
      }
    }
  }, [hasWizardData, isLoaded, autoCreating, nameTaken, userMemberships, autoCreateOrg]);

  if (!isLoaded || autoCreating) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="text-muted-foreground mx-auto h-8 w-8 animate-spin" />
          <p className="text-muted-foreground mt-4 text-sm">
            {autoCreating ? 'Setting up your account...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  // If the user has a Clerk org WITH a linked Tagada account, redirect to it.
  // When `accountUnlinked` is set (member of a Clerk org that has no `accounts`
  // row), we must NOT bounce to `/org/:slug` — that route would just redirect
  // back here (the AccessRestrictionGuard fires on `noAccountLinked`), an
  // infinite loop. Instead we fall through and render the account-setup flow.
  const firstOrg = userMemberships?.data?.[0]?.organization;
  if (firstOrg?.slug && !accountUnlinked) {
    return <Navigate to={`/org/${firstOrg.slug}`} replace />;
  }

  const handleAccountCreated = (orgSlug: string) => {
    window.location.href = getPostAuthOrgPath(orgSlug, false);
  };

  const handlePostSignupOnboardingComplete = () => {
    setPostSignupOnboardingDone();
    setShowCreateOrg(true);
  };

  if (!showCreateOrg) {
    return (
      <div className="bg-background h-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-3 sm:p-6">
          <div className="flex w-full max-w-7xl flex-col items-center space-y-6">
            <Card className="w-full p-4 sm:p-8 lg:p-10">
              <CardContent className="flex flex-col items-center p-0">
                <PostSignUpOnboarding onComplete={handlePostSignupOnboardingComplete} />
              </CardContent>
            </Card>
            <div className="text-center">
              <Button
                variant="link"
                onClick={() => {
                  resetAnalytics();
                  signOut();
                }}
                size="sm"
                className="text-muted-foreground"
              >
                Sign out and try a different account
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background h-screen overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="w-full max-w-2xl space-y-4">
          <Card className="p-6 sm:p-8">
            <CardContent className="p-0">
              <CreateAccount onAccountCreated={handleAccountCreated} />
            </CardContent>
          </Card>

          <div className="text-center">
            <Button
              variant="link"
              onClick={() => {
                resetAnalytics();
                signOut();
              }}
              size="sm"
              className="text-muted-foreground"
            >
              Sign out and try a different account
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Guard wrapper — shows a loading spinner until Clerk confirms
 * the user truly has no organizations, then renders NoAccountPage.
 * If the user does have an org AND a usable Tagada account, redirects to /
 * immediately. A member of an org with no linked account is kept here
 * (rendering NoAccountPage) instead of being bounced back to / — which
 * would just redirect here again (infinite loop).
 */
export default function NoOrganizationGuard() {
  const location = useLocation();
  const { userMemberships } = useOrganizationList({
    userMemberships: { pageSize: 1 },
  });
  const { noAccountLinked, isLoading: accountLoading } = useAccountRestrictions();
  const cameFromNoAccount =
    (location.state as { noAccountLinked?: boolean } | null)?.noAccountLinked === true;

  if (userMemberships.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="text-muted-foreground mx-auto h-8 w-8 animate-spin" />
          <p className="text-muted-foreground mt-4 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  const hasOrg = (userMemberships?.data?.length ?? 0) > 0;
  if (!hasOrg) {
    return <NoAccountPage />;
  }

  if (cameFromNoAccount || noAccountLinked) {
    return <NoAccountPage accountUnlinked />;
  }

  if (accountLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="text-muted-foreground mx-auto h-8 w-8 animate-spin" />
          <p className="text-muted-foreground mt-4 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return <Navigate to="/" replace />;
}
